import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
} from "framer-motion";
import Navigation from "../../components/Navigation";
import Seo, { breadcrumbs } from "../../components/Seo";
import { absUrl } from "../../lib/seo";
import { MetaLabel, CornerTick } from "../../brand/Brand";
import { subscribePublishedCaseStudies } from "../../caseStudies/publicApi";
import {
  EASE,
  ProgressBar,
  Reveal,
  RiseLines,
  isDark,
} from "./anim";
import "./Work.css";

/* Derive a coarse filter label from the industry field ("E-commerce · Cycling"
 * → "E-commerce"). Filters only render when there are 2+ distinct values. */
const filterOf = (cs) =>
  (cs.industry || "").split("·")[0].trim() || null;

const Work = () => {
  const reduced = useReducedMotion();
  const [items, setItems] = useState(null); // null = loading
  const [filter, setFilter] = useState("All");

  useEffect(() => subscribePublishedCaseStudies(setItems), []);
  // Braces matter: newer Chrome returns a Promise from window.scrollTo,
  // and a concise arrow would hand that Promise to React as a "cleanup fn"
  // → "TypeError: destroy is not a function" on unmount.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filters = useMemo(() => {
    const set = new Set((items || []).map(filterOf).filter(Boolean));
    return set.size > 1 ? ["All", ...set] : [];
  }, [items]);

  const visible = useMemo(
    () =>
      (items || []).filter(
        (cs) => filter === "All" || filterOf(cs) === filter
      ),
    [items, filter]
  );

  /* Cursor-following preview card (desktop, fine pointers only) */
  const [active, setActive] = useState(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const px = useSpring(mx, { stiffness: 260, damping: 28, mass: 0.6 });
  const py = useSpring(my, { stiffness: 260, damping: 28, mass: 0.6 });
  const listRef = useRef(null);
  const onMove = (e) => {
    const r = listRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set(e.clientX - r.left);
    my.set(e.clientY - r.top);
  };

  const count = items ? String(items.length).padStart(2, "0") : "··";

  return (
    <div className="wk-page">
      <Seo
        title="Work — bboy creative"
        description="Selected case studies from bboy creative — web apps, mobile apps, and MVPs, designed and shipped."
        path="/work"
        jsonLd={[
          {
            "@type": "CollectionPage",
            "@id": absUrl("/work#collection"),
            name: "Work — bboy creative",
            description:
              "Selected case studies from bboy creative — web apps, mobile apps, and MVPs, designed and shipped.",
            url: absUrl("/work"),
            // Listing the cases explicitly gives the crawler the full set even
            // if it never renders the client-side list.
            hasPart: (items || []).map((item) => ({
              "@type": "CreativeWork",
              name: item.title,
              url: absUrl(`/work/${item.id}`),
            })),
          },
          breadcrumbs([
            { name: "Home", path: "/" },
            { name: "Work", path: "/work" },
          ]),
        ]}
      />

      <ProgressBar />
      <Navigation />

      {/* ── Header ─────────────────────────────────────────────────── */}
      <header className="wk-header">
        <div className="wk-container wk-frame">
          <CornerTick pos="tl" />
          <CornerTick pos="tr" />
          <CornerTick pos="bl" />
          <CornerTick pos="br" />

          <div className="wk-header-meta">
            <MetaLabel>Index / Selected work</MetaLabel>
            <MetaLabel>({count}) case studies</MetaLabel>
          </div>

          <div className="wk-header-title">
            <RiseLines as="h1" className="wk-h1" lines={["Work"]} />
            <motion.span
              className="wk-h1-dot"
              aria-hidden="true"
              initial={reduced ? false : { scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, ease: EASE, delay: 0.7 }}
            >
              .
            </motion.span>
          </div>

          <Reveal delay={0.25}>
            <p className="wk-sub">
              <em>Proof over promises</em> — every project below shipped to
              real users. Built by one engineer and one designer, start to
              finish.
            </p>
          </Reveal>

          {filters.length > 0 && (
            <Reveal delay={0.35}>
              <div className="wk-filters" role="tablist" aria-label="Filter case studies">
                {filters.map((f) => (
                  <button
                    key={f}
                    role="tab"
                    aria-selected={filter === f}
                    className={`wk-filter ${filter === f ? "is-active" : ""}`}
                    onClick={() => setFilter(f)}
                  >
                    {filter === f && (
                      <motion.span
                        layoutId="wk-filter-pill"
                        className="wk-filter-pill"
                        transition={{ duration: 0.45, ease: EASE }}
                      />
                    )}
                    <span className="wk-filter-label">{f}</span>
                  </button>
                ))}
              </div>
            </Reveal>
          )}
        </div>
      </header>

      {/* ── List ───────────────────────────────────────────────────── */}
      <main className="wk-container">
        {items === null ? (
          <div className="wk-skeleton" aria-label="Loading case studies">
            {[0, 1, 2, 3].map((i) => (
              <div className="wk-skeleton-row" key={i} style={{ animationDelay: `${i * 0.12}s` }} />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div className="wk-empty">
            <MetaLabel>Nothing here yet</MetaLabel>
            <p>Case studies are being written up. Check back soon.</p>
          </div>
        ) : (
          <div
            className="wk-list"
            ref={listRef}
            onMouseMove={onMove}
            onMouseLeave={() => setActive(null)}
          >
            {/* Floating preview follows the cursor (desktop only) */}
            <AnimatePresence>
              {active && (
                <motion.div
                  key={active.id}
                  className="wk-preview"
                  style={{ x: px, y: py, background: active.color || "#1f1f1f" }}
                  initial={{ opacity: 0, scale: 0.7, rotate: -4 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  exit={{ opacity: 0, scale: 0.7, rotate: 4 }}
                  transition={{ duration: 0.35, ease: EASE }}
                  aria-hidden="true"
                >
                  {active.heroImageUrl ? (
                    <img src={active.heroImageUrl} alt="" />
                  ) : (
                    <span className="wk-preview-tag">{active.tag}</span>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {visible.map((cs, i) => (
              <Reveal key={cs.id} delay={Math.min(i * 0.06, 0.3)} amount={0.2}>
                <Link
                  to={`/work/${cs.id}`}
                  className="wk-row"
                  style={{
                    "--row-c": cs.color || "#1f1f1f",
                    "--row-fg": isDark(cs.color) ? "var(--fw-cream)" : "var(--fw-ink)",
                  }}
                  onMouseEnter={() => setActive(cs)}
                  onFocus={() => setActive(cs)}
                  onBlur={() => setActive(null)}
                >
                  <span className="wk-row-num">
                    {String(i + 1).padStart(3, "0")}
                  </span>
                  <span className="wk-row-main">
                    <span className="wk-row-title">{cs.title}</span>
                    <span className="wk-row-desc">{cs.description}</span>
                  </span>
                  <span className="wk-row-meta">
                    <span className="wk-row-industry">{cs.industry || cs.tag}</span>
                    <span className="wk-row-year">{cs.year || ""}</span>
                  </span>
                  <span className="wk-row-arrow" aria-hidden="true">
                    →
                  </span>

                  {/* Mobile card face (hidden on desktop) */}
                  <span className="wk-card" aria-hidden="true">
                    {cs.heroImageUrl && (
                      <img className="wk-card-img" src={cs.heroImageUrl} alt="" loading="lazy" />
                    )}
                    <span className="wk-card-top">
                      <span className="wk-card-tag">{cs.tag}</span>
                      <span className="wk-card-year">{cs.year || ""}</span>
                    </span>
                    <span className="wk-card-bottom">
                      <span className="wk-card-title">{cs.title}</span>
                      <span className="wk-card-desc">{cs.description}</span>
                    </span>
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </main>

      {/* ── End cap ────────────────────────────────────────────────── */}
      <section className="wk-endcap">
        <div className="wk-container wk-frame">
          <CornerTick pos="tl" color="var(--fw-cream)" />
          <CornerTick pos="br" color="var(--fw-cream)" />
          <Reveal>
            <MetaLabel color="rgba(243,239,232,0.7)">
              Yours could be next
            </MetaLabel>
            <h2 className="wk-endcap-title">
              Got a thing to&nbsp;build?
            </h2>
            <a href="/start" className="wk-endcap-cta">
              Start a project →
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Work;
